import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { toast } from '../../store/toastStore';
import { useUndoableMutation } from '../../hooks/useUndoableMutation';
import { apiErrorMessage } from '../sales/shared';
import { appLocale } from '../../utils/format';
import { formatClock } from '../home/homeModel';
import { shouldOpenCloseDay } from '../documentation/documentationModel';
import { fetchSuggestionsCached } from '../documentation/useWorkLogSuggestions';
import CloseDayDialog from '../documentation/CloseDayDialog';
import BreakTimersDialog from './BreakTimersDialog';
import { invalidateAttendanceViews } from './attendanceInvalidate';
import { registerAttendanceFlowHost } from './attendanceFlow';
import { shouldOpenEndOfDay } from './endOfShiftModel';
import type { ActiveTimer } from '../../types';

/**
 * P-10e (D-85, D-63) — the ONE place that runs "הפסקה" and "החתמת יציאה"
 * (`requestBreak` / `requestClockOut` from attendanceFlow), mounted once in
 * `GlobalTimer` next to `TimerFlowHost`. The automatic stop of every timer,
 * without a question, is gone: a running timer always opens the window first.
 */
export default function AttendanceFlowHost() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const locale = appLocale(i18n.language);
  const [breakOpen, setBreakOpen] = useState(false);
  const [closeDay, setCloseDay] = useState<{ timers: ActiveTimer[] } | null>(null);
  const busyRef = useRef(false);

  const invalidate = () => invalidateAttendanceViews(queryClient);

  // B.23 — the clock-out is a delayed commit with "ביטול" (as before).
  const clockOut = useUndoableMutation<void>({
    mutationFn: () => api.clockOut(),
    // DQ-115: closing the tab inside the 6 s window still clocks out (`fetch` + keepalive, same request as api.clockOut).
    unload: () => ({ method: 'POST', path: '/attendance/clock-out', body: {} }),
    message: () => t('home.attendance.clockedOutAt', { time: formatClock(new Date().toISOString(), locale) }),
    errorMessage: (err) => apiErrorMessage(err, t('attendance.clockOutFailed')),
    onSchedule: () => {
      queryClient.setQueryData(['attendance-status'], (old: { data?: Record<string, unknown> } | undefined) =>
        old?.data ? { ...old, data: { ...old.data, status: 'clocked_out' } } : old);
    },
    // FIX-ATT: undo (or a failed commit) puts the real server state back on every attendance view.
    onUndo: () => invalidateAttendanceViews(queryClient),
    onSuccess: () => invalidate(),
  });
  const clockOutRef = useRef(clockOut);
  clockOutRef.current = clockOut;

  const freshTimers = async (): Promise<ActiveTimer[]> => {
    try {
      const status = await queryClient.fetchQuery({
        queryKey: ['timer-status'],
        queryFn: () => api.getTimerStatus(),
        staleTime: 0,
      });
      return status?.data?.timers ?? [];
    } catch {
      return [];
    }
  };

  /** D-342 — start the break: the server PAUSES every running timer (no `stop`), the logs stay open. */
  const startBreak = async () => {
    try {
      await api.startBreak([]);
      setBreakOpen(false);
      toast('success', t('timer.breakWindow.startedKept'));
    } catch (err) {
      toast('error', apiErrorMessage(err, t('attendance.breakStartFailed')));
    } finally {
      invalidate();
    }
  };
  const startBreakRef = useRef(startBreak);
  startBreakRef.current = startBreak;

  useEffect(() => registerAttendanceFlowHost({
    requestBreak: async () => {
      if (busyRef.current) return;
      busyRef.current = true;
      try {
        const timers = await freshTimers();
        if (timers.length === 0) await startBreakRef.current();
        else setBreakOpen(true);
      } finally {
        busyRef.current = false;
      }
    },
    requestClockOut: async () => {
      if (busyRef.current) return;
      busyRef.current = true;
      try {
        const [timers, gapOpens] = await Promise.all([
          freshTimers(),
          fetchSuggestionsCached(queryClient)
            .then((day) => shouldOpenCloseDay({
              suggestions: day.suggestions.length,
              attendanceMinutes: day.attendance_minutes,
              documentedMinutes: day.documented_minutes,
            }))
            .catch(() => false),
        ]);
        if (shouldOpenEndOfDay({ timers: timers.length, gapOpens })) setCloseDay({ timers });
        else clockOutRef.current.mutate();
      } finally {
        busyRef.current = false;
      }
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  return (
    <>
      {breakOpen && (
        <BreakTimersDialog
          onBreak={() => startBreak()}
          onCancel={() => { setBreakOpen(false); toast('info', t('timer.breakWindow.cancelled')); }}
        />
      )}
      {closeDay && (
        <CloseDayDialog
          open
          timers={closeDay.timers}
          onCancel={() => setCloseDay(null)}
          onClockOut={() => {
            setCloseDay(null);
            clockOut.mutate();
          }}
        />
      )}
    </>
  );
}
